import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { AxiosError } from 'axios'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Loader2, LockKeyhole } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AuthLayout } from '@/features/auth/auth-layout'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useFormField,
} from '@/components/ui/form'
import { PasswordInput } from '@/components/password-input'
import { cn } from '@/shared/lib/utils'
import { useChangePassword } from '@/shared/hooks/api/user'

const changePasswordFormSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, 'auth:changePassword.validation.currentPasswordRequired'),
    newPassword: z
      .string()
      .min(8, 'auth:changePassword.validation.newPasswordMin'),
    confirmPassword: z
      .string()
      .min(1, 'auth:changePassword.validation.confirmPasswordRequired'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'auth:changePassword.validation.passwordsMismatch',
    path: ['confirmPassword'],
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'auth:changePassword.validation.newPasswordMustDiffer',
    path: ['newPassword'],
  })

function TranslatedFormMessage({
  className,
}: React.ComponentProps<'p'>) {
  const { t } = useTranslation()
  const { error, formMessageId } = useFormField()
  const body = error?.message ? t(error.message as string) : null
  if (!body) {
    return null
  }
  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn('text-sm text-destructive', className)}
    >
      {body}
    </p>
  )
}

export function ChangePassword() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const { mutateAsync: changePassword, isPending: isChangingPassword } =
    useChangePassword()
  const [serverError, setServerError] = useState<string | null>(null)

  const form = useForm<z.infer<typeof changePasswordFormSchema>>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onSubmit = useCallback(
    async (data: z.infer<typeof changePasswordFormSchema>) => {
      setServerError(null)
      try {
        await changePassword({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        })
        navigate('/', { replace: true })
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 401) {
          setServerError('changePassword.errors.invalidCurrentPassword')
          return
        }
        setServerError('changePassword.errors.unableToChangePassword')
      }
    },
    [changePassword, navigate]
  )

  return (
    <AuthLayout>
      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="text-lg tracking-tight">
            {t('changePassword.title')}
          </CardTitle>
          <CardDescription>{t('changePassword.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {serverError ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{t(serverError)}</AlertDescription>
            </Alert>
          ) : null}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid gap-3"
            >
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('changePassword.fields.currentPassword.label')}</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="********" {...field} />
                    </FormControl>
                    <TranslatedFormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('changePassword.fields.newPassword.label')}</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="********" {...field} />
                    </FormControl>
                    <TranslatedFormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('changePassword.fields.confirmPassword.label')}</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="********" {...field} />
                    </FormControl>
                    <TranslatedFormMessage />
                  </FormItem>
                )}
              />
              <Button
                className="mt-2"
                type="submit"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <LockKeyhole />
                )}
                {t('changePassword.actions.submit')}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
